import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, doc, getDocs, getDoc, addDoc, updateDoc, deleteDoc, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../../FirebaseConfig';
import axios from 'axios';
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListSubheader,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Box,
  TextField,
  InputAdornment,
  Typography,
  Dialog,  // Dialog for pop-up window to manage call
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'; // Updated import for Material-UI components
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Call as CallIcon,
  Email as EmailIcon,
  History as HistoryIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import ContactForm from './ContactForm'; // Importing the ContactForm component
import CallOptionsDialog from './CallOptionsDialog'; // New component we'll create
import FlagCallModal from './FlagCallModal';

const Contacts = ({ currentUser, userPhoneNumber, onClose, isPhoneAgentMode, onViewHistory }) => {
  const [contacts, setContacts] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [callOptionsOpen, setCallOptionsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const sectionRefs = useRef({});
  const listContainerRef = useRef(null);

  const ALPHABET = ['#', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M',
    'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

  const getSectionLetter = (name) => {
    if (!name || !name.trim()) return '#';
    const first = name.trim()[0].toUpperCase();
    return /[A-Z]/.test(first) ? first : '#';
  };

  const { grouped, lettersWithContacts } = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = q
      ? contacts.filter((c) => (c.contact_name || '').toLowerCase().includes(q))
      : contacts;

    const groups = {};
    filtered.forEach((c) => {
      const letter = getSectionLetter(c.contact_name);
      if (!groups[letter]) groups[letter] = [];
      groups[letter].push(c);
    });

    Object.keys(groups).forEach((letter) => {
      groups[letter].sort((a, b) =>
        (a.contact_name || '').localeCompare(b.contact_name || '', undefined, { sensitivity: 'base' })
      );
    });

    return { grouped: groups, lettersWithContacts: new Set(Object.keys(groups)) };
  }, [contacts, searchQuery]);

  const handleLetterClick = (letter) => {
    const node = sectionRefs.current[letter];
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    if (currentUser) {
      const fetchContacts = async () => {
        const contactsRef = collection(db, "users", currentUser.uid, "contacts");
        const querySnapshot = await getDocs(contactsRef);
        setContacts(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      };

      fetchContacts();
    }
  }, [currentUser]);

  const handleMenuOpen = (event, contact) => {
    event.preventDefault(); // Prevent default behavior that might interfere
    if (event.currentTarget) {
      setAnchorEl(event.currentTarget); // Set anchorEl to the button being clicked
      setSelectedContact(contact);
      console.log("anchorEl set to:", event.currentTarget);  // Log the element to check validity
    } else {
      console.error("Invalid anchorEl:", event.currentTarget);
    }
  };  

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddContact = () => {
    setSelectedContact(null);  // Clear the selection when adding a new contact
    setShowForm(true);
  };

  const handleEdit = () => {
    setShowForm(true);  // Show the form for editing
    handleMenuClose(); // Close the menu after setting the contact for editing
  };

  const handleBackToList = () => {
    setShowForm(false);  // Return to the contact list
  };

  const handleSaveContact = async (newContact) => {
    setLoading(true);
    try {
      const contactsRef = collection(db, "users", currentUser.uid, "contacts");

      if (selectedContact && selectedContact.id) { // If editing, update the existing contact
        const contactDocRef = doc(contactsRef, selectedContact.id);
        await updateDoc(contactDocRef, newContact);
      } else {
        const docRef = await addDoc(contactsRef, newContact); // If adding a new contact
        newContact.id = docRef.id; // New Line: Capture the generated document ID and store it as `contact_id`
      }

      // Refresh the contact list after saving
      const querySnapshot = await getDocs(contactsRef);
      setContacts(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setShowForm(false);
    } catch (error) {
      console.error("Error saving contact: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (currentUser && contactId) {
      setLoading(true); // Set loading state to true
      try {
        const contactsRef = collection(db, "users", currentUser.uid, "contacts");
        const contactDocRef = doc(contactsRef, contactId);
        await deleteDoc(contactDocRef); // Delete the contact from Firestore
  
        // Refresh the contact list after deletion
        const querySnapshot = await getDocs(contactsRef);
        setContacts(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setShowForm(false); // Go back to the contact list after deletion
      } catch (error) {
        console.error("Error deleting contact: ", error);
      } finally {
        setLoading(false); // Set loading state back to false
      }
    }
  };  

  const handleCall = (contact) => {
    console.log("handleCall triggered", contact);
    setSelectedContact(contact);
    handleMenuClose(); // Close the menu
    if (isPhoneAgentMode) {
      console.log("Phone Agent Mode is active, opening CallOptionsDialog");
      setCallOptionsOpen(true);
    } else {
      console.log("Regular call mode, initiating call directly");
      initiateCall(contact, "get live agent");
    }
  };

  const initiateCall = async (contact, instructions) => {
    // Validate required fields before making the call
    if (!contact.contact_phone || !contact.contact_phone.trim()) {
      alert("Cannot place call: This contact has no phone number. Please edit the contact and add a phone number.");
      return;
    }
    if (!userPhoneNumber || !userPhoneNumber.trim()) {
      alert("Cannot place call: Your phone number is not set. Please update your profile with a phone number.");
      return;
    }

    try {
      // Clear previous call progress immediately
      setCallLog([]);
      setCallStatus('');
      setTakenOver(false);

      // Clear Firestore call progress before initiating new call
      const userRef = doc(db, "users", currentUser.uid);
      await updateDoc(userRef, { call_progress_log: [] });

      const requestPayload = {
        uid: currentUser.uid,
        contact_phone: contact.contact_phone,
        user_phone_number: userPhoneNumber,
        contact_id: contact.id,
        user_instructions: instructions
      };
      console.log("Full request payload:", requestPayload);

      const response = await axios.post(
        'https://us-central1-maia-tech.cloudfunctions.net/callContact_TwilioConf', requestPayload);

      console.log("Call initiated with response:", response.data);
      setCallDialogOpen(true);
    } catch (error) {
      console.error("Error initiating call:", error);
      const errorMessage = error.response?.data?.error || "Failed to initiate call. Please check the contact's phone number and try again.";
      alert(errorMessage);
    }
  }; 

  const [callStatus, setCallStatus] = useState('');
  const [takingOver, setTakingOver] = useState(false);
  const [endingCall, setEndingCall] = useState(false);
  const [callLog, setCallLog] = useState([]);
  const callLogRef = React.useRef(null);

  // Listen for live call transcript updates when call dialog is open
  useEffect(() => {
    if (!callDialogOpen || !currentUser) {
      setCallLog([]);
      return;
    }

    // Listen for real-time call progress updates stored on the user document
    console.log("Setting up call_progress listener for uid:", currentUser.uid);
    const userRef = doc(db, "users", currentUser.uid);
    const unsub = onSnapshot(userRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const log = data.call_progress_log || [];
        if (log.length > 0) {
          console.log("Call progress entries:", log.length);
          setCallLog(log);
          setTimeout(() => {
            if (callLogRef.current) {
              callLogRef.current.scrollTop = callLogRef.current.scrollHeight;
            }
          }, 100);
        }
      }
    }, (error) => {
      console.error("call_progress listener error:", error);
    });

    return () => unsub();
  }, [callDialogOpen, currentUser]);

  const [takenOver, setTakenOver] = useState(false);
  // Explicit "user manually ended the call" signal. Necessary because the
  // text-pattern match below misses post-handoff cases: once Maia hands off
  // to a live agent, the voice agent stops writing to call_progress_log, so
  // when the user later clicks End Call the API succeeds but no transcript
  // entry matches "ended". Without this flag, callEnded stays false forever
  // and the recap block / X-close / disabled-action-buttons never appear.
  // Reported by Tim 2026-04-27 testing United Healthcare handoff call.
  const [manuallyEnded, setManuallyEnded] = useState(false);

  const callEnded = manuallyEnded || callLog.some(entry => {
    const text = (entry.text || '').toLowerCase();
    return text.includes('has ended') || text.includes('call ended') || text.includes('ended.');
  });

  // Post-call recap state — populated by a Firestore listener that watches for
  // the call_history doc the voice agent writes in its finally block. Keyed by
  // contact_phone (most recent doc for this user-contact pair) since we don't
  // reliably track the call_history_id on the WebUI side. Edge case: rapid
  // back-to-back calls to the same contact could mismatch by one call —
  // acceptable for v1; revisit if it becomes a problem.
  const [recap, setRecap] = useState(null); // { id, summary, outcome, timestamp } | null
  const [recapError, setRecapError] = useState(null); // user-facing string | null
  const [recapTimedOut, setRecapTimedOut] = useState(false);
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [flagToast, setFlagToast] = useState(false);
  const [flagPendingHint, setFlagPendingHint] = useState(false);

  // Reset all post-call state whenever the dialog re-opens for a new call.
  // (callDialogOpen flipping false → true means a fresh call.)
  useEffect(() => {
    if (callDialogOpen) {
      setRecap(null);
      setRecapError(null);
      setRecapTimedOut(false);
      setFlagToast(false);
      setFlagPendingHint(false);
      setManuallyEnded(false);
      setTakenOver(false);
    }
  }, [callDialogOpen]);

  // Watch the user's most-recent call doc for natural termination — i.e.,
  // the call ended without the user clicking End Call (contact hung up, user
  // hung up their phone, goal_achieved auto-end fired, voice agent finally
  // block ran, etc.). Two terminal signals on the calls/{callSid} doc:
  //   isActive: true → false   (voice agent finally block)
  //   contact_call_status: 'completed'/'failed'/'busy'/'no-answer'/'canceled'
  //                              (Twilio status callback to /twilio-status)
  // Either is sufficient to flip manuallyEnded and render the recap UI.
  //
  // Earlier version used a 3-field composite query (`where uid where isActive
  // orderBy created limit 1`) which failed silently in production despite
  // the matching composite index existing. Switched to index-free
  // `where uid == X` and sort client-side. Tim's call collection is small
  // enough (<100 docs) that this is fine for the dialog's lifetime.
  const sawActiveRef = useRef(false);
  useEffect(() => {
    if (!callDialogOpen || !currentUser) return;
    sawActiveRef.current = false;
    const callsRef = collection(db, 'calls');
    // Narrow to the most-recent single call doc via the (uid, created DESC)
    // composite index. Earlier version pulled ALL Tim's calls (806 docs at
    // last count) and sorted client-side, which dominated the
    // snapshot-delivery latency Tim was seeing on natural call end. Now
    // it's one doc per snapshot — the only meaningful latency is Twilio's
    // status-callback timing.
    const q = query(
      callsRef,
      where('uid', '==', currentUser.uid),
      orderBy('created', 'desc'),
      limit(1)
    );
    const TERMINAL_CALL_STATUSES = new Set([
      'completed', 'failed', 'busy', 'no-answer', 'canceled',
    ]);
    const unsub = onSnapshot(q, (snap) => {
      if (snap.empty) return;
      const mostRecent = snap.docs[0];
      const data = mostRecent.data();
      const isActive = data.isActive;
      const status = data.contact_call_status;
      // Once we observe this call active, remember it so a subsequent
      // terminal transition fires manuallyEnded.
      if (isActive === true) {
        sawActiveRef.current = true;
      }
      const isTerminal =
        sawActiveRef.current &&
        (isActive === false || (status && TERMINAL_CALL_STATUSES.has(status)));
      if (isTerminal) {
        setManuallyEnded(true);
        setCallStatus((prev) => prev || 'Call ended.');
      }
    }, (err) => {
      // Surface the failure visibly via the recap-error state — same pattern
      // as the recap listener. Without this, a silent index/permissions
      // error would let natural-end detection break invisibly.
      console.error('[active-call] listener error', err);
      setRecapError(
        err?.code === 'failed-precondition'
          ? "Couldn't watch for call end (Firestore index missing — please report)."
          : "Couldn't watch for call end. Use the End Call button to dismiss when finished."
      );
    });
    return () => unsub();
  }, [callDialogOpen, currentUser]);

  useEffect(() => {
    // Only listen once the call has actually ended AND we know which contact
    // this dialog is for. Avoid the listener running on every contact-list
    // open by gating on callEnded too.
    if (!callDialogOpen || !callEnded || !currentUser || !selectedContact?.contact_phone) {
      return;
    }
    setRecap(null); // reset until the doc lands
    setRecapError(null);
    // Match on the raw stored phone string, NOT a digits-only cleaned form.
    // The voice agent writes call_history.contact_phone verbatim from the
    // contact's contact_phone field — so for a contact stored as
    // '+1 800-323-2323' the call_history doc also has '+1 800-323-2323'.
    // An earlier version of this listener cleaned to digits-only, which
    // silently broke the where-equals match for any contact stored with
    // formatting (dashes / country code / spaces).
    const queryPhone = selectedContact.contact_phone;
    const historyRef = collection(db, 'users', currentUser.uid, 'call_history');
    // Deliberately NOT using orderBy('timestamp') in this query — that
    // would require a composite index on (contact_phone, timestamp) which
    // doesn't exist yet. The where-equals query alone is index-free; we
    // sort client-side over the docs that match. For most contacts the
    // result set is tiny (a handful of past calls). If volume ever
    // outgrows that we can add the composite index in firestore.indexes.json.
    const q = query(historyRef, where('contact_phone', '==', queryPhone));
    const unsub = onSnapshot(q, (snap) => {
      if (snap.empty) return;
      // Pick the doc with the latest timestamp client-side. Skip docs whose
      // timestamp hasn't been set by the server yet (server timestamps land
      // null on the local listener for one or two ticks).
      let bestDoc = null;
      let bestMs = -1;
      snap.docs.forEach((d) => {
        const ts = d.data().timestamp;
        const ms = ts && ts.toMillis ? ts.toMillis() : 0;
        if (ms > bestMs) {
          bestMs = ms;
          bestDoc = d;
        }
      });
      if (!bestDoc) return;
      const d = bestDoc.data();
      setRecap({
        id: bestDoc.id,
        summary: d.summary || '',
        outcome: d.outcome || '',
        timestamp: d.timestamp,
      });
    }, (err) => {
      console.error('post-call recap listener error', err);
      // Surface the failure to the user instead of swallowing it silently.
      // Firestore's "missing composite index" error has code
      // 'failed-precondition' — it's the case that bit us 2026-04-26 (twice:
      // once on the call-history-tab-empty bug, once on this listener's
      // earlier orderBy). For other errors, fall back to a generic message
      // so the user still sees something happened.
      const isMissingIndex = err?.code === 'failed-precondition';
      setRecapError(
        isMissingIndex
          ? "Couldn't load this call's record (Firestore index missing — please report this)."
          : "Couldn't load this call's record. Please refresh and try again."
      );
    });
    return () => unsub();
  }, [callDialogOpen, callEnded, currentUser, selectedContact]);

  // Defensive max-wait — if the call_history doc still hasn't landed 10s
  // after the call ended, surface a clearer message rather than leaving the
  // user stuck on the "Saving this call's details" hint forever. Edge case
  // but covers voice-agent crashes / redactor failures / any other path
  // where the doc never gets written.
  useEffect(() => {
    if (!callEnded || recap?.id || recapError) return;
    const t = setTimeout(() => {
      // Only set timeout state if we still don't have a recap. The dialog may
      // have unmounted in the meantime; the cleanup function below covers that.
      setRecapTimedOut(true);
    }, 10_000);
    return () => clearTimeout(t);
  }, [callEnded, recap, recapError]);

  const handleTakeOver = async () => {
    if (currentUser) {
      setTakingOver(true);
      setCallStatus('Connecting you now...');
      try {
        // Try ConversationRelay takeover first
        await axios.post('https://us-central1-maia-tech.cloudfunctions.net/end_TwilioConf', {
          uid: currentUser.uid,
          endCall: false
        });

        // Also try the voice agent takeover endpoint for ConversationRelay calls
        try {
          await axios.post('https://maia-voice-agent-370081006843.us-central1.run.app/takeover/' + currentUser.uid);
        } catch (e) {
          // May not be a ConversationRelay call — that's okay
        }
        setCallStatus('You are now connected. Maia has been removed.');
        setTakenOver(true);

        // Add takeover message to call progress
        const userRef = doc(db, "users", currentUser.uid);
        const userDoc = await getDoc(userRef);
        const log = userDoc.data()?.call_progress_log || [];
        log.push({ role: "maia", text: "Call handed over to you. You are now speaking directly." });
        await updateDoc(userRef, { call_progress_log: log });
      } catch (error) {
        console.error("Error taking over call: ", error);
        setCallStatus('Failed to take over. Please try again.');
      } finally {
        setTakingOver(false);
      }
    }
  };

  const handleEndCall = async () => {
    if (currentUser) {
      setEndingCall(true);
      setCallStatus('Ending call...');
      try {
        await axios.post('https://us-central1-maia-tech.cloudfunctions.net/end_TwilioConf', {
          uid: currentUser.uid,
          endCall: true
        });

        // Also end any ConversationRelay session
        try {
          await axios.post('https://maia-voice-agent-370081006843.us-central1.run.app/takeover/' + currentUser.uid);
        } catch (e) {
          // May not be a ConversationRelay call — that's okay
        }

        setCallStatus('Call ended.');
        setManuallyEnded(true);  // flips callEnded → true so the recap / X / disabled-buttons render
      } catch (error) {
        console.error("Error ending call:", error);
        setCallStatus('Failed to end call.');
      } finally {
        setEndingCall(false);
        // NOTE: previously auto-closed the dialog after 1.5s. That was fine
        // when there was no post-call content, but it now races the recap
        // block — the "Help improve Maia" button only flashed on screen
        // before the dialog yanked itself closed. Dismissal is now explicit
        // via the X icon in the dialog header (added 2026-04-26 alongside
        // the recap block) or by clicking outside (the dialog's onClose
        // handler already allows that once callEnded is true).
      }
    }
  };

  return (
    <div>
      {showForm ? (
        <div>
          <ContactForm
            contact={selectedContact}  
            onSave={handleSaveContact}
            onBack={handleBackToList}  
            onCancel={handleBackToList}
            onDelete={handleDeleteContact} 
            loading={loading}
          />
        </div>
      ) : (
        <div>
          <TextField
            fullWidth
            size="small"
            placeholder="Search contacts"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')} aria-label="clear search">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            {/* Alphabet nav on the LEFT (three-dot menu is on the right of each row).
                Sticky so it stays visible while the parent tab-content div scrolls. */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                pr: 0.5,
                userSelect: 'none',
                flexShrink: 0,
                position: 'sticky',
                top: 0,
                alignSelf: 'flex-start',
              }}
            >
              {ALPHABET.map((letter) => {
                const enabled = lettersWithContacts.has(letter);
                return (
                  <Box
                    key={letter}
                    component="button"
                    type="button"
                    onClick={() => enabled && handleLetterClick(letter)}
                    disabled={!enabled}
                    aria-label={`Jump to ${letter}`}
                    sx={{
                      fontSize: '10px',
                      lineHeight: '14px',
                      padding: '0 4px',
                      border: 'none',
                      background: 'transparent',
                      color: enabled ? 'primary.main' : 'text.disabled',
                      cursor: enabled ? 'pointer' : 'default',
                      fontWeight: enabled ? 700 : 400,
                      '&:focus': { outline: 'none' },
                    }}
                  >
                    {letter}
                  </Box>
                );
              })}
            </Box>
            {/* Contact list - grows naturally, scroll is handled by parent .settingsModal-tab-content */}
            <Box ref={listContainerRef} sx={{ flex: 1, minWidth: 0 }}>
              {Object.keys(grouped).length === 0 ? (
                <Typography sx={{ p: 2, color: 'text.secondary', fontStyle: 'italic' }}>
                  {searchQuery ? 'No contacts match' : 'No contacts yet'}
                </Typography>
              ) : (
                <List sx={{ py: 0 }}>
                  {ALPHABET.filter((l) => grouped[l]).map((letter) => (
                    <Box
                      key={letter}
                      ref={(el) => { sectionRefs.current[letter] = el; }}
                    >
                      <ListSubheader
                        component="div"
                        sx={{
                          backgroundColor: '#f5f5f5',
                          color: 'text.secondary',
                          fontWeight: 700,
                          lineHeight: '28px',
                        }}
                      >
                        {letter}
                      </ListSubheader>
                      {grouped[letter].map((contact) => (
                        <ListItem key={contact.id}>
                          <ListItemText primary={contact.contact_name || '(no name)'} />
                          <ListItemSecondaryAction>
                            <IconButton edge="end" onClick={(event) => handleMenuOpen(event, contact)}>
                              <MoreVertIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </Box>
                  ))}
                </List>
              )}
            </Box>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleEdit}>
              <EditIcon /> Edit
            </MenuItem>
            <MenuItem onClick={() => handleCall(selectedContact)}>
              <CallIcon /> Call
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>
              <EmailIcon /> Email
            </MenuItem>
            {onViewHistory && (
              <MenuItem onClick={() => { onViewHistory(selectedContact?.contact_name); handleMenuClose(); }}>
                <HistoryIcon /> History
              </MenuItem>
            )}
          </Menu>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddContact}
            startIcon={<AddIcon />}
            sx={{ mt: 2, mb: 1 }}
          >
            Add Contact
          </Button>
          {/* Call Management Dialog
              Disable close-on-backdrop and close-on-escape while the call is
              still active — losing the Manage Call popup mid-call strands the
              user with no way to Take Over / End Call / Stop Maia. Only the
              explicit Close / End Call / Take Over buttons should dismiss it.
              Once the call has ended (callEnded flag), a click outside is
              harmless and the dialog closes normally. */}
          <Dialog
            open={callDialogOpen}
            onClose={(event, reason) => {
              if (!callEnded && (reason === "backdropClick" || reason === "escapeKeyDown")) {
                return;  // ignore — keep popup open while call is live
              }
              setCallDialogOpen(false);
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ pr: 6 }}>
              Manage Call
              {/* Once the call has ended, surface an explicit X-close in the
                  top-right. The dialog historically had no visible close
                  affordance post-call (the action buttons disabled to "Call
                  Ended" and the user had to click outside), which was
                  confusing. While the call is live we deliberately don't show
                  this — losing the dialog mid-call strands the user with no
                  way to Take Over / End Call. */}
              {callEnded && (
                <IconButton
                  aria-label="close"
                  onClick={() => setCallDialogOpen(false)}
                  sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.600' }}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </DialogTitle>
            <DialogContent>
              {/* Top status line — three states. callEnded wins over takenOver
                  because the user could take over and then end. The transient
                  callStatus line below is only shown for in-flight states
                  ('Connecting you now…', 'Ending call…', 'Failed…'); once
                  we're in a stable terminal state, the top line says it all. */}
              {callEnded ? (
                <DialogContentText sx={{ fontWeight: 'bold', color: 'green' }}>
                  You have ended the call with {selectedContact?.contact_name}.
                </DialogContentText>
              ) : takenOver ? (
                <DialogContentText sx={{ fontWeight: 'bold' }}>
                  You have taken over the call with {selectedContact?.contact_name}.
                </DialogContentText>
              ) : (
                <DialogContentText>
                  Maia is handling your call with {selectedContact?.contact_name}.
                </DialogContentText>
              )}
              {callStatus && !callEnded && !takenOver && (
                <DialogContentText style={{ marginTop: '10px', fontWeight: 'bold', color: callStatus.includes('Failed') ? 'red' : 'green' }}>
                  {callStatus}
                </DialogContentText>
              )}

              {/* Post-call recap block — surfaces the flag affordance
                  immediately when the call ends. Earlier iteration also
                  rendered the Phase 4 LLM summary here, but that gated the
                  flag button on a Firestore doc that can take seconds to
                  appear (and sometimes fails to generate at all on edge
                  cases), which made the button unreliable. The summary is
                  available in the Call History tab; this block stays
                  minimal so the flag stays reachable.

                  The recap listener still runs silently to populate
                  recap.id (the call_history_id the FlagCallModal needs).
                  If the user clicks before the doc lands, we surface a
                  short retry hint rather than block. */}
              {callEnded && !flagToast && (
                <Box sx={{
                  mt: 2,
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  backgroundColor: '#fafafa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ color: '#444' }}>
                      Anything Maia could do better?
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.5,
                        color: '#777',
                        fontStyle: 'italic',
                        lineHeight: 1.4,
                      }}
                    >
                      (please tell us, especially if Maia did not achieve the goal of this call.)
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      if (!recap?.id) {
                        // Doc hasn't landed yet — surface a hint and let the
                        // user retry in a beat. No spinner; the listener
                        // will populate recap.id within a few seconds.
                        setFlagPendingHint(true);
                        return;
                      }
                      setFlagPendingHint(false);
                      setFlagModalOpen(true);
                    }}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                    disabled={Boolean(recapError) || (recapTimedOut && !recap?.id)}
                  >
                    Help improve Maia
                  </Button>
                </Box>
              )}
              {/* Pre-resolution hint: the user clicked the button before the
                  call_history doc had landed. Listener still running; resolution
                  typically arrives within a few seconds. */}
              {callEnded && flagPendingHint && !recap?.id && !recapTimedOut && !recapError && !flagToast && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, ml: 0.5, color: '#666', fontStyle: 'italic' }}>
                  Saving this call's details — try again in a moment.
                </Typography>
              )}
              {/* Listener error — surface visibly. Used to be gated on
                  callEnded, which was useless for the exact failure mode
                  we hit 2026-04-27 (active-call listener errored at mount
                  with a missing-index error → callEnded never flipped →
                  error never showed). Now renders any time we have a
                  listener error, regardless of call state. */}
              {recapError && !flagToast && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, ml: 0.5, color: '#c62828' }}>
                  {recapError}
                </Typography>
              )}
              {/* Defensive max-wait — voice agent finally block didn't land
                  the call_history doc within 10s. User can still flag from
                  Call History; explain. */}
              {callEnded && recapTimedOut && !recap?.id && !recapError && !flagToast && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, ml: 0.5, color: '#666', fontStyle: 'italic' }}>
                  Couldn't load this call's record — you can still flag it from Settings → Call History.
                </Typography>
              )}
              {callEnded && flagToast && (
                <Box sx={{
                  mt: 2,
                  p: 2,
                  border: '1px solid #c8e6c9',
                  borderRadius: 1,
                  backgroundColor: '#f1f8e9',
                }}>
                  <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                    Thanks — your flag was submitted. The Maia team will use it
                    to improve.
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleTakeOver} color="primary" disabled={takingOver || endingCall || takenOver || callEnded}>
                {callEnded ? 'Call Ended' : takenOver ? 'Connected' : takingOver ? 'Connecting...' : 'Take Over Call'}
              </Button>
              <Button onClick={handleEndCall} color="error" disabled={takingOver || endingCall || callEnded}>
                {callEnded ? 'Call Ended' : endingCall ? 'Ending...' : 'End Call'}
              </Button>
            </DialogActions>
            {/* Live Call Progress */}
            <div style={{ padding: '0 24px 16px' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>
                Call Progress
              </div>
              <div
                ref={callLogRef}
                style={{
                  minHeight: '150px',
                  maxHeight: 'min(400px, 40vh)',
                  overflowY: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '12px',
                  backgroundColor: '#fafafa',
                  fontSize: '13px',
                  lineHeight: '1.6',
                }}
              >
                {callLog.length === 0 ? (
                  <div style={{ color: '#999', fontStyle: 'italic' }}>Waiting for call to connect...</div>
                ) : (
                  callLog.map((entry, idx) => (
                    <div key={idx} style={{ marginBottom: '6px' }}>
                      {entry.role === 'contact' ? (
                        <div><span style={{ color: '#1976d2', fontWeight: 'bold' }}>Contact:</span> {entry.text}</div>
                      ) : (
                        <div><span style={{ color: '#388e3c', fontWeight: 'bold' }}>Maia:</span> {entry.text}</div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </Dialog>
          {/* Call Options Dialog */}
          <CallOptionsDialog
            open={callOptionsOpen}
            onClose={() => setCallOptionsOpen(false)}
            onConfirm={(instructions) => {
              console.log("Instructions confirmed in CallOptionsDialog:", instructions); // Log instructions received
              initiateCall(selectedContact, instructions);
              setCallOptionsOpen(false);
            }}
            isPhoneAgentMode={isPhoneAgentMode}
          />

          {/* Flag-this-call modal — opened from the post-call recap block in
              the Manage Call dialog. Uses the same /flag-call endpoint as the
              CallHistory tab's per-row button. */}
          <FlagCallModal
            open={flagModalOpen}
            onClose={() => setFlagModalOpen(false)}
            currentUser={currentUser}
            callHistoryId={recap?.id}
            contactName={selectedContact?.contact_name}
            onFlagged={() => setFlagToast(true)}
          />
        </div>
      )}
    </div>
  );
};

export default Contacts;
